import { BaseChartPresetHandler } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import {
    PresetChartType,
    DEFAULT_SHAPE_PROPS,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import { LineChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/line-chart';
import { ChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2';
import { canvasToEmu } from '@src/slide/infrastructure/module/independent/unit';
import type {
    SeriesInitialProps,
    SeriesPresetPoint,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { ILineChart } from '../../../Interface/chart-v3/sub-chart';
import { STGrouping, STMarkerStyleDmlC, STSchemeColorVal } from '../../../Interface/chart-v3/sdk-model.interface';
import { IBaseSubChart } from '../../../Interface/chart-v3/sub-chart/base-sub-chart.interface';
import { LineChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/line-chart-series';
import { IChartSpace } from '../../../Interface/chart-v3/chart-properties.interface';

export class LineChartPresetHandler extends BaseChartPresetHandler {
    get ChartSeries() {
        return LineChartSeries;
    }
    get SubChart() {
        return LineChart;
    }

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): ILineChart {
        const subChart = super.createChartModel(type, chartSpace, initialProps) as ILineChart;

        subChart.smooth = false;

        switch (type) {
            case PresetChartType.LINE_STANDARD:
                this.setLineStandard(subChart);
                break;
            case PresetChartType.LINE_STACKED:
                this.setLineStacked(subChart);
                break;
            case PresetChartType.LINE_PERCENT_STACKED:
                this.setLinePercentStacked(subChart);
                break;
            case PresetChartType.LINE_STANDARD_WITH_POINT:
                this.setLineStandardWithPoint(subChart);
                break;
            case PresetChartType.LINE_STACKED_WITH_POINT:
                this.setLineStackedWithPoint(subChart);
                break;
            case PresetChartType.LINE_PERCENT_STACKED_WITH_POINT:
                this.setLinePercentStackedWithPoint(subChart);
                break;
        }

        return subChart;
    }

    protected shouldExtendTrendline(targetType: PresetChartType) {
        return targetType === PresetChartType.LINE_STANDARD || targetType === PresetChartType.LINE_STANDARD_WITH_POINT;
    }

    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        const baseProps = super.generatePresetSeriesInitialProps(seriesIndex, points, seriesName, showDataLabels);
        return {
            ...baseProps,
            props: {
                ...baseProps.props,
                outline: {
                    cap: 'rnd',
                    lineJoin: 'round',
                    width: canvasToEmu(4.5),
                },
            },
            smooth: 0,
        };
    }

    protected extendSeries(params: {
        newSer: ChartSeries;
        oldSer: ChartSeries;
        newSubChart: IBaseSubChart;
        oldSubChart: IBaseSubChart;
        newSubChartType: PresetChartType;
        serIndex: number;
    }) {
        super.extendSeries(params);

        const { newSer, oldSer } = params;
        // 按office的逻辑，如果之前图形设置了无边框，就在修改图表的时候移除NoFill属性，否则折线会不显示
        if (oldSer.props?.style?.border?.noFill) {
            delete newSer.props.style.border.noFill;
        }

        newSer.dataList.forEach((point, pointIndex) => {
            const uiDataPoint = point.dataPoint;

            if (uiDataPoint?.props?.style?.border?.noFill) {
                delete uiDataPoint.props.style.border.noFill;
            }

            // newSubChart没有sdk数据，所以这里用params.oldSubChart来判断是否是主题色
            this.removeDataPointUIDataSchemeClr(uiDataPoint, params.oldSubChart, params.serIndex, pointIndex);
        });
    }

    protected getStyle(newSer, oldSer, newSubChart: IBaseSubChart, oldSubChart: IBaseSubChart) {
        const style = super.getStyle(newSer, oldSer, newSubChart, oldSubChart);
        delete style.fillBackground;
        return style;
    }

    // 用于进行"修改"是否有数据点操作
    private setLineMarker(subChart: ILineChart, hasMarker: boolean) {
        subChart.marker = hasMarker;

        if (!hasMarker) {
            subChart.series?.forEach((ser) => {
                ser.marker = {
                    symbol: STMarkerStyleDmlC.none,
                    props: DEFAULT_SHAPE_PROPS,
                };
            });
        } else {
            subChart.series?.forEach((ser, index) => {
                const colorValue = `accent${index + 1}`;
                ser.marker = {
                    symbol: STMarkerStyleDmlC.circle,
                    size: 5,
                    // 这块自行继承主题色
                    props: {
                        style: {
                            border: {
                                width: 1.5,
                                color: {
                                    colorValue,
                                    colorType: 'scheme',
                                },
                            },
                            fillBackground: {
                                solidFill: {
                                    color: {
                                        colorValue,
                                        colorType: 'scheme',
                                    },
                                },
                            },
                        },
                    },
                };
            });
        }
    }

    // 以下都拆成对于"图表类型"应用相关的一些接口，便于在"创建"和"修改"图表都能够一定程度上复用
    // 设置为"折线图"
    private setLineStandard(subChart: ILineChart) {
        subChart.grouping = STGrouping.standard;
        this.setLineMarker(subChart, false);
    }
    // 设置为"堆积折线图"
    private setLineStacked(subChart: ILineChart) {
        subChart.grouping = STGrouping.stacked;
        this.setLineMarker(subChart, false);
    }
    // 设置为"百分比堆积折线图"
    private setLinePercentStacked(subChart: ILineChart) {
        subChart.grouping = STGrouping.percentStacked;
        this.setLineMarker(subChart, false);
    }
    // 设置为"带点的堆积折线图"
    private setLineStandardWithPoint(subChart: ILineChart) {
        subChart.grouping = STGrouping.standard;
        this.setLineMarker(subChart, true);
    }
    // 设置为"带点的堆积折线图"
    private setLineStackedWithPoint(subChart: ILineChart) {
        subChart.grouping = STGrouping.stacked;
        this.setLineMarker(subChart, true);
    }
    // 设置为"带点的百分比堆积折线图"
    private setLinePercentStackedWithPoint(subChart: ILineChart) {
        subChart.grouping = STGrouping.percentStacked;
        this.setLineMarker(subChart, true);
    }
}
